import React from "react";
import { Table, Badge, Button, Spinner, Alert } from "react-bootstrap";
import { IoEye, IoCreate, IoTrash } from "react-icons/io5";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

const ArtikelTable = ({
  artikel = [],
  loading = false,
  error = null,
  pagination = {},
  meta = {},
  currentSortField = null,
  currentSortOrder = "DESC",
  onSort = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onView = () => {},
}) => {
  // Format tanggal
  
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Render badge untuk status
  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: { variant: "warning", label: "Draft" },
      PUBLISHED: { variant: "success", label: "Published" },
      ARCHIVED: { variant: "secondary", label: "Archived" },
    };
    const config = statusConfig[status] || { variant: "light", label: status };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  // Render badge untuk type
  const getTypeBadge = (type) => {
    const typeConfig = {
      TEKNOLOGI: { variant: "info", label: "Teknologi" },
      PENDIDIKAN: { variant: "primary", label: "Pendidikan" },
      KESEHATAN: { variant: "danger", label: "Kesehatan" },
      BISNIS: { variant: "success", label: "Bisnis" },
      OLAHRAGA: { variant: "warning", label: "Olahraga" },
    };
    const config = typeConfig[type] || { variant: "light", label: type };
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  // Render icon sort
  const renderSortIcon = (field) => {
    if (currentSortField !== field) return <FaSort className="text-muted" />;
    return currentSortOrder === "ASC" ? <FaSortUp /> : <FaSortDown />;
  };

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading artikel...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="danger" className="my-4">
        <Alert.Heading>Error Loading Data</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  // Empty state
  if (!loading && !error && artikel.length === 0) {
    return (
      <div className="text-center py-5 border rounded">
        <p className="text-muted mb-2">No artikel found</p>
        {pagination.search && (
          <small className="text-muted">
            Try adjusting your search or filter
          </small>
        )}
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <Table hover striped>
        <thead>
          <tr>
            <th style={{ width: "35%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold"
                onClick={() => onSort("judul")}
              >
                Judul {renderSortIcon("judul")}
              </Button>
            </th>
            <th style={{ width: "15%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold"
                onClick={() => onSort("type_artikel")}
              >
                Type {renderSortIcon("type_artikel")}
              </Button>
            </th>
            <th style={{ width: "10%" }}>
              <span className="fw-semibold">Status</span>
            </th>
            <th style={{ width: "10%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold"
                onClick={() => onSort("views")}
              >
                Post {renderSortIcon("type_konten")}
              </Button>
            </th>
            <th style={{ width: "15%" }}>
              <Button
                variant="link"
                className="p-0 text-decoration-none text-dark fw-semibold"
                onClick={() => onSort("created_at")}
              >
                Created {renderSortIcon("created_at")}
              </Button>
            </th>
            <th style={{ width: "15%" }} className="text-center">
              <span className="fw-semibold">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {artikel.map((item) => (
            <tr key={item.id}>
              <td>
                <div className="d-flex flex-column">
                  <strong className="mb-1">{item.judul}</strong>
                  {item.ringkasan && (
                    <small className="text-muted">
                      {item.ringkasan.substring(0, 100)}
                      {item.ringkasan.length > 100 && "..."}
                    </small>
                  )}
                </div>
              </td>
              <td>{getTypeBadge(item.type_artikel)}</td>
              <td>{getStatusBadge(item.status)}</td>
              <td>
                <Badge bg="light" text="dark" className="px-3 py-2">
                  {item.type_konten}
                </Badge>
              </td>
              <td>
                <small className="text-muted">
                  {formatDate(item.created_at)}
                </small>
              </td>
              <td>
                <div className="d-flex justify-content-center gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    title="View Details"
                    onClick={() => onView(item)}
                  >
                    <IoEye />
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    title="Edit Artikel"
                    onClick={() => onEdit(item)}
                  >
                    <IoCreate />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    title="Delete Artikel"
                    onClick={() => onDelete(item.id)}
                  >
                    <IoTrash />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ArtikelTable;
